/**
 * Toast utility — single source of truth for toast styling and configuration.
 * Configures react-hot-toast to render solid, opaque toasts that stand out
 * on glass surfaces.
 *
 * Usage:
 *   - Import TOASTER_PROPS in App.tsx and spread onto <Toaster />
 *   - All existing toast.success() / toast.error() calls automatically
 *     pick up the styling — no changes needed in 40+ consumer files.
 */

import type { ToasterProps } from 'react-hot-toast';

/* ── Color tokens ─────────────────────────────────── */

export const TOAST_COLORS = {
  // Backgrounds — fully opaque, strong tints
  successBg: '#dcfce7',
  errorBg: '#fee2e2',
  defaultBg: '#ffffff',

  // Borders — visible, solid
  successBorder: '#86efac',
  errorBorder: '#fca5a5',
  defaultBorder: '#cbd5e1',

  // Icon accent
  successIcon: '#16a34a',
  errorIcon: '#dc2626',

  // Text
  text: '#0f172a',
} as const;

/* ── Shared base style ────────────────────────────── */

const baseStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: '14px',
  fontWeight: 700,
  color: TOAST_COLORS.text,
  borderRadius: '14px',
  padding: '14px 18px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.1)',
  maxWidth: '420px',
};

/* ── Toaster props (spread onto <Toaster /> in App.tsx) ── */

export const TOASTER_PROPS: ToasterProps = {
  position: 'top-center',
  toastOptions: {
    duration: 4000,
    style: {
      ...baseStyle,
      background: TOAST_COLORS.defaultBg,
      border: `1px solid ${TOAST_COLORS.defaultBorder}`,
    },
    success: {
      style: {
        ...baseStyle,
        background: TOAST_COLORS.successBg,
        border: `1px solid ${TOAST_COLORS.successBorder}`,
      },
      iconTheme: {
        primary: TOAST_COLORS.successIcon,
        secondary: '#ffffff',
      },
    },
    error: {
      style: {
        ...baseStyle,
        background: TOAST_COLORS.errorBg,
        border: `1px solid ${TOAST_COLORS.errorBorder}`,
      },
      iconTheme: {
        primary: TOAST_COLORS.errorIcon,
        secondary: '#ffffff',
      },
    },
  },
};
